// Analytics building blocks shared by the Dashboard (last 30 days, all widgets)
// and the Analytics page (any period, any widget): the stats hook, headline
// numbers, the drop-off funnel, the activity-over-time chart and the per-widget
// table. Counts are unique visitor sessions (see 100-api functions/stats).
import { Fragment, useEffect, useRef, useState } from "react";
import { useApi, type FunnelStage, type Stats, type StatsRange, type Widget } from "../api";
import "../dashboard.css";
import { productParam, type ProductFilter } from "./ProductSwitch";

// Periods available (stats are kept for a year, then deleted)
export const RANGES: { value: StatsRange; label: string; long: string }[] = [
  { value: "7d", label: "7 days", long: "Last 7 days" },
  { value: "30d", label: "30 days", long: "Last 30 days" },
  { value: "90d", label: "90 days", long: "Last 90 days" },
  { value: "180d", label: "180 days", long: "Last 180 days" },
  { value: "1y", label: "1 year", long: "Last 12 months" },
];
export const rangeLabel = (r: StatsRange) => RANGES.find((x) => x.value === r)!.long;

// Plain-English names for each funnel stage
export const STAGE_LABELS: Record<FunnelStage, string> = {
  loaded: "Calculator loaded",
  opened: "Opened the calculator",
  postcode: "Searched a postcode",
  address: "Picked their address",
  scan: "Had their roof scanned",
  quote: "Saw a price",
  contact: "Reached the contact form",
  lead: "Sent their details",
  detailed: "Asked for a detailed quote",
};

export const fmt = (n: number) => n.toLocaleString("en-GB");

/** True if the user has asked their system for less motion (then nothing animates). */
const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * A number that counts up from 0 (or from its previous value) with an ease-out,
 * so headline figures land with a little flourish. Final value straight away
 * for reduced motion.
 */
export function CountUp({ value, duration = 900, delay = 0 }: { value: number; duration?: number; delay?: number }) {
  const [shown, setShown] = useState(reducedMotion() ? value : 0);
  const from = useRef(0);
  useEffect(() => {
    if (reducedMotion()) {
      setShown(value);
      return;
    }
    const start = from.current;
    let raf = 0;
    let t0: number | null = null;
    const tick = (now: number) => {
      t0 ??= now + delay;
      const p = Math.min(1, Math.max(0, (now - t0) / duration));
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      setShown(Math.round(start + (value - start) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else from.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration, delay]);
  return <>{fmt(shown)}</>;
}
/** Percentage, or null when there's nothing to divide by (shown as a dash, never a fake 0%). */
export const pct = (n: number, of: number) => (of > 0 ? `${Math.round((n / of) * 1000) / 10}%` : null);

/** Load stats for a period (and optionally one widget); reloads when either changes. */
export function useStats(range: StatsRange, widgetId?: string, product: ProductFilter = "all") {
  const api = useApi();
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setError(null);
    api
      .getStats(range, widgetId || undefined, productParam(product))
      .then((s) => live && setStats(s))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [api, range, widgetId, product]);
  return { stats, error };
}

/** Four headline numbers; values are dashes until the stats arrive. */
export function KpiRow({ stats, period, product = "all" }: { stats: Stats | null; period: string; product?: ProductFilter }) {
  const t = stats?.totals;
  const kpis: { label: string; value: number | null; note: string }[] = [
    { label: "Calculator loads", value: t ? t.loaded : null, note: period },
    // Solar keeps no per-home records (only its callbacks), so it counts roofs scanned instead
    product === "solar"
      ? { label: "Roofs scanned", value: t ? (t.scan ?? 0) : null, note: t ? `${fmt(t.lead)} asked for a callback` : period }
      : {
          label: "Homes looked up",
          value: stats ? stats.homes.total : null,
          note: stats ? `${fmt(stats.homes.callbacks)} asked for a callback` : period,
        },
    { label: "Prices shown", value: t ? t.quote : null, note: period },
    { label: "Enquiries", value: t ? t.lead : null, note: t ? `${pct(t.lead, t.quote) ?? "—"} of prices shown` : period },
  ];
  return (
    <section className="kpi-row" aria-label="Headline numbers">
      {kpis.map((k, i) => (
        // Cards rise in one after another; the numbers count up
        <div key={k.label} className="card kpi" style={{ ["--i" as string]: i }}>
          <div className="kpi-label">{k.label}</div>
          <div className="kpi-value">{k.value === null ? "—" : <CountUp value={k.value} delay={i * 90} />}</div>
          <div className="kpi-note">{k.note}</div>
        </div>
      ))}
    </section>
  );
}

/**
 * Stage totals, leaving out some components (contact forms: they have no
 * postcode or price steps, so their enquiries would inflate the last step).
 */
function totalsWithout(stats: Stats, exclude?: Set<string>): Stats["totals"] {
  if (!exclude?.size) return stats.totals;
  const t = Object.fromEntries(stats.stages.map((s) => [s, 0])) as Stats["totals"];
  for (const w of stats.widgets) if (!exclude.has(w.widgetId)) for (const s of stats.stages) t[s] += w[s] ?? 0;
  return t;
}

/**
 * Visitor journey: one horizontal bar per stage, as a share of widget
 * loads, with the count and the drop from the stage before. "Opened" only
 * applies to floating widgets, so it's hidden when nobody has opened one; "scan"
 * (roof scanned) only to solar estimates, hidden the same way.
 * The last step splits into what the lead asked for (a callback or a detailed
 * quote): indented rows that add up to it, shown for QuoteFlow companies or
 * whenever a detailed quote was asked for.
 */
export function Funnel({
  stats,
  period,
  exclude,
  quoteFlow = false,
  product = "all",
}: {
  stats: Stats;
  period: string;
  exclude?: Set<string>;
  quoteFlow?: boolean;
  product?: ProductFilter;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const t = totalsWithout(stats, exclude);
  // "Roof scanned" only applies to solar estimates, so it's hidden when no roof was scanned
  const stages = stats.stages.filter((s) => s !== "detailed" && (s !== "opened" || t.opened > 0) && (s !== "scan" || (t.scan ?? 0) > 0));
  const top = t.loaded;
  const detailed = t.detailed ?? 0;
  const split = quoteFlow || detailed > 0;
  // What the leads asked for (older leads, before this was recorded, count as callbacks)
  const subRows = [
    { key: "callback", label: "Asked for a callback", value: Math.max(0, t.lead - detailed) },
    { key: "detailed", label: STAGE_LABELS.detailed, value: Math.min(detailed, t.lead) },
  ];

  return (
    <section className="card funnel-card">
      <h2>{product === "solar" ? "Solar estimates" : product === "ashp" ? "ASHP Estimators" : "Estimators"} – Visitor journey</h2>
      <p className="card-sub">Visitors reaching each step, {period.toLowerCase()}. Each visitor counts once per step.</p>
      {top === 0 ? (
        <p className="muted">No calculator loads in this period yet.</p>
      ) : (
        <ol className="funnel">
          {stages.map((s, i) => {
            const prev = i > 0 ? t[stages[i - 1]] : null;
            const lost = prev !== null ? prev - t[s] : 0;
            return (
              <Fragment key={s}>
                <li
                  className={`funnel-row${hover === s ? " is-hover" : ""}`}
                  style={{ ["--i" as string]: i }}
                  onMouseEnter={() => setHover(s)}
                  onMouseLeave={() => setHover(null)}
                >
                  <span className="funnel-label">{STAGE_LABELS[s]}</span>
                  <span className="funnel-track" aria-hidden="true">
                    {/* A sliver stays visible for tiny non-zero counts */}
                    <span className="funnel-bar" style={{ width: `${Math.max((t[s] / top) * 100, t[s] > 0 ? 0.5 : 0)}%` }} />
                  </span>
                  <span className="funnel-value">
                    <strong>
                      <CountUp value={t[s]} delay={i * 80} />
                    </strong>{" "}
                    <span className="muted">{pct(t[s], top)}</span>
                  </span>
                  <span className="funnel-drop">{prev !== null && lost > 0 ? `−${fmt(lost)} (${pct(lost, prev)})` : ""}</span>
                  {hover === s && (
                    <span className="funnel-tip" role="tooltip">
                      <strong>{STAGE_LABELS[s]}</strong>
                      {fmt(t[s])} visitors · {pct(t[s], top)} of loads
                      {prev !== null && <> · {pct(t[s], prev) ?? "—"} of the step before</>}
                    </span>
                  )}
                </li>
                {s === "lead" &&
                  split &&
                  subRows.map((r, j) => (
                    // Not further steps: the last step split by what was asked for
                    <li
                      key={r.key}
                      className={`funnel-row funnel-sub${hover === r.key ? " is-hover" : ""}`}
                      style={{ ["--i" as string]: i + j + 1 }}
                      onMouseEnter={() => setHover(r.key)}
                      onMouseLeave={() => setHover(null)}
                    >
                      <span className="funnel-label">{r.label}</span>
                      <span className="funnel-track" aria-hidden="true">
                        <span className="funnel-bar" style={{ width: `${Math.max((r.value / top) * 100, r.value > 0 ? 0.5 : 0)}%` }} />
                      </span>
                      <span className="funnel-value">
                        <strong>{fmt(r.value)}</strong> <span className="muted">{pct(r.value, t.lead)}</span>
                      </span>
                      <span className="funnel-drop">of those who sent details</span>
                      {hover === r.key && (
                        <span className="funnel-tip" role="tooltip">
                          <strong>{r.label}</strong>
                          {fmt(r.value)} of {fmt(t.lead)} who sent their details · {pct(r.value, top)} of loads
                        </span>
                      )}
                    </li>
                  ))}
              </Fragment>
            );
          })}
        </ol>
      )}
    </section>
  );
}

// Stages worth charting over time (one at a time: they differ by orders of magnitude)
const TREND_STAGES: FunnelStage[] = ["loaded", "postcode", "quote", "lead"];
const TREND_LABELS: Partial<Record<FunnelStage, string>> = { loaded: "Loads", postcode: "Postcode searches", quote: "Prices shown", lead: "Enquiries" };

/**
 * Activity over time for one chosen stage: a column per day, or per week for
 * 90 days and longer (so a year is 53 readable columns, not 365 slivers).
 */
export function TrendChart({ stats }: { stats: Stats }) {
  const [stage, setStage] = useState<FunnelStage>("loaded");
  const [hover, setHover] = useState<number | null>(null);
  const weekly = stats.daily.length > 60;

  // Bucket the zero-filled daily series (oldest first) into days or 7-day weeks
  const buckets: { start: string; end: string; value: number }[] = [];
  const size = weekly ? 7 : 1;
  for (let i = 0; i < stats.daily.length; i += size) {
    const chunk = stats.daily.slice(i, i + size);
    buckets.push({ start: chunk[0].day, end: chunk[chunk.length - 1].day, value: chunk.reduce((n, d) => n + d[stage], 0) });
  }
  const max = Math.max(...buckets.map((b) => b.value), 0);
  const dayFmt = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  const bucketLabel = (b: { start: string; end: string }) => (b.start === b.end ? dayFmt(b.start) : `${dayFmt(b.start)} – ${dayFmt(b.end)}`);
  // A handful of evenly spaced axis labels, always including the first and last
  const tickEvery = Math.max(1, Math.ceil(buckets.length / 6));

  return (
    <section className="card trend-card">
      <div className="trend-head">
        <div>
          <h2>{TREND_LABELS[stage]} over time</h2>
          <p className="card-sub">{weekly ? "Per week" : "Per day"}, UTC.</p>
        </div>
        <div className="ed-segmented" role="group" aria-label="Measure">
          {TREND_STAGES.map((s) => (
            <button key={s} type="button" className={s === stage ? "is-on" : ""} aria-pressed={s === stage} onClick={() => setStage(s)}>
              {TREND_LABELS[s]}
            </button>
          ))}
        </div>
      </div>
      {max === 0 ? (
        <p className="muted">Nothing recorded in this period yet.</p>
      ) : (
        <div className="trend" role="img" aria-label={`${TREND_LABELS[stage]} ${weekly ? "per week" : "per day"}, peak ${fmt(max)}`}>
          <div className="trend-plot">
            <span className="trend-max">{fmt(max)}</span>
            {buckets.map((b, i) => (
              // Full-height hit target; the visible column sits at its foot
              <div
                key={`${stage}-${b.start}`}
                className={`trend-col${hover === i ? " is-hover" : ""}`}
                style={{ ["--i" as string]: i, ["--n" as string]: buckets.length }}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              >
                <span className="trend-bar" style={{ height: `${(b.value / max) * 100}%` }} />
                {hover === i && (
                  <span className={`trend-tip${i > buckets.length / 2 ? " is-left" : ""}`} role="tooltip">
                    <strong>{bucketLabel(b)}</strong>
                    {fmt(b.value)} {TREND_LABELS[stage]!.toLowerCase()}
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className="trend-axis" aria-hidden="true">
            {buckets.map((b, i) => (
              <span key={b.start}>{i % tickEvery === 0 || i === buckets.length - 1 ? dayFmt(b.start) : ""}</span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/** The same numbers per widget (a table rather than a chart: exact values, many widgets). */
export function WidgetTable({ stats, widgets }: { stats: Stats; widgets: Widget[] }) {
  const names = new Map(widgets.map((w) => [w.widgetId, w.name]));
  const rows = [...stats.widgets].sort((a, b) => b.loaded - a.loaded);
  return (
    <section className="card">
      <h2>By component</h2>
      {rows.length === 0 ? (
        <p className="muted">No activity in this period yet.</p>
      ) : (
        <div className="table-scroll">
          <table className="stats-table">
            <thead>
              <tr>
                <th scope="col">Component</th>
                <th scope="col">Loads</th>
                <th scope="col">Postcodes</th>
                <th scope="col">Addresses</th>
                <th scope="col">Prices shown</th>
                <th scope="col">Contact form</th>
                <th scope="col">Enquiries</th>
                <th scope="col">Load → enquiry</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.widgetId}>
                  <th scope="row">{names.get(r.widgetId) ?? "Deleted component"}</th>
                  <td>{fmt(r.loaded)}</td>
                  <td>{fmt(r.postcode)}</td>
                  <td>{fmt(r.address)}</td>
                  <td>{fmt(r.quote)}</td>
                  <td>{fmt(r.contact)}</td>
                  <td>{fmt(r.lead)}</td>
                  <td>{pct(r.lead, r.loaded) ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
